import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";

export default function SplashScreen({ onComplete }) {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const xOffset = isMobile ? 0 : 100;

    gsap.to(".logo-container", {
      x: xOffset,
      y: 0,
      delay: 0.4,
      opacity: 1,
      duration: 1.8,
      ease: "power2.out",
    });

    gsap.to(".welcome-text", {
      x: isMobile ? 0 : -100,
      y: 0,
      delay: 0.4,
      opacity: 1,
      duration: 1.8,
      ease: "power2.out",
    });

    gsap.to(".tagline", {
      x: xOffset,
      y: 0,
      delay: 0.4,
      opacity: 1,
      duration: 1.8,
      ease: "power2.out",
    });

    gsap.to(".loading-bar-container", {
      x: xOffset,
      y: 0,
      delay: 0.4,
      opacity: 1,
      duration: 1.8,
      ease: "power2.out",
    });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
      setTimeout(() => {
        if (onComplete) {
          onComplete();
        }
      }, 1000);
    }, 4200);

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {showSplash && (
        <motion.div
          className="splash-screen"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        >
          <div className="logo-container">
            <motion.svg
              width="80"
              height="80"
              viewBox="0 0 100 100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <motion.path
                d="M30,20 L70,20 L70,80 L30,80 Z"
                fill="none"
                stroke="#3B82F6"
                strokeWidth="5"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1, delay: 1 }}
              />
            </motion.svg>
          </div>
          <h1 className="welcome-text">
            Welcome to <span>ITSoftlab</span>
          </h1>
          <p className="tagline">Transforming Ideas into Digital Reality</p>
          <div className="loading-bar-container">
            <motion.div
              className="loading-bar"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ delay: 1, duration: 1.6 }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const style = `
  .splash-screen {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    background: linear-gradient(135deg, #1d8ef0 0%, #18a39c 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    top: 0;
    left: 0;
    padding: 20px;
    box-sizing: border-box;
    overflow: hidden;
  }

  .logo-container {
    width: 120px;
    height: 120px;
    opacity: 0;
    margin-right: 200px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }

  .welcome-text {
    font-size: clamp(1.6rem, 4vw, 2.2rem);
    font-weight: bold;
    color: white;
    margin-bottom: 1rem;
    margin-left: 200px;
    opacity: 0;
    text-align: center;
    line-height: 1.25;
    max-width: 90vw;
  }

  .welcome-text span {
    color: rgb(0, 225, 255);
  }

  .tagline {
    font-size: clamp(0.95rem, 2.2vw, 1.2rem);
    color: #bfdbfe;
    opacity: 0;
    max-width: 500px;
    text-align: center;
    margin-right: 200px;
    line-height: 1.5;
    padding: 0 12px;
  }

  .loading-bar-container {
    width: 256px;
    max-width: 80vw;
    height: 5px;
    opacity: 0;
    background: rgba(30, 64, 175, 0.4);
    margin-top: 2.5rem;
    border-radius: 999px;
    margin-right: 200px;
    overflow: hidden;
  }

  .loading-bar {
    height: 100%;
    background: rgb(0, 225, 255);
    width: 0%;
    border-radius: 999px;
  }

  @media (max-width: 768px) {
    .logo-container {
      width: 96px;
      height: 96px;
      margin-right: 0 !important;
      margin-bottom: 1.5rem;
    }

    .welcome-text {
      margin-left: 0 !important;
      font-size: 1.6rem;
      padding: 0 16px;
    }

    .tagline {
      margin-right: 0 !important;
      font-size: 1rem;
      padding: 0 16px;
    }

    .loading-bar-container {
      margin-right: 0 !important;
      margin-top: 2rem;
      width: 200px;
    }
  }

  @media (max-width: 380px) {
    .logo-container {
      width: 80px;
      height: 80px;
    }
    .welcome-text {
      font-size: 1.35rem;
    }
    .tagline {
      font-size: 0.9rem;
    }
  }
`;

if (typeof document !== "undefined") {
  const existing = document.getElementById("splash-screen-style");
  if (!existing) {
    const styleSheet = document.createElement("style");
    styleSheet.id = "splash-screen-style";
    styleSheet.type = "text/css";
    styleSheet.innerText = style;
    document.head.appendChild(styleSheet);
  }
}
